import { ResourceCredential } from '../../../api';
import { LinkedCredential } from '../../models';
import type { ApiClient, CredentialDescriptor, VerificationResult } from '../../types';
import { generateKey } from '../../utils/idempotency';
import { CredentialFlavor } from '../flavor';
import type { CredentialBlob, CredentialMetadata } from '../types';

export class VerifiableCredential {
    protected flavor = new CredentialFlavor();

    constructor(
        protected client: ApiClient,
        public descriptor: CredentialDescriptor,
        protected blob?: CredentialBlob,
    ) {}

    toLinkedCredential(): LinkedCredential<unknown> {
        return new LinkedCredential(this.client, this.descriptor.id, this.descriptor.data.digestMultibase);
    }

    async getBlob(): Promise<CredentialBlob> {
        if (!this.blob) {
            const response = await this.client.credentials.credentialDownload(
                this.descriptor.id,
                this.descriptor.revision,
            );

            this.blob = await response.blob();
        }

        return this.blob;
    }

    async getMetaData(): Promise<CredentialMetadata> {
        return this.flavor.getMetadata(await this.getBlob());
    }

    async verify(): Promise<VerificationResult> {
        const result = await this.client.credentials.credentialVerify(this.descriptor.id);

        return {
            verified: result.verified || false,
        };
    }

    async send(targetId: string, privateKeyId: string): Promise<void> {
        const { VpDecorator } = await import('../../presentation'); // using dynamic import to break circular dependency

        const vpDecorator = new VpDecorator(this.client);
        const vpPresentation = await vpDecorator.issue([this], privateKeyId);

        return vpPresentation.send(targetId, privateKeyId);
    }

    async delete(): Promise<void> {
        await this.client.credentials.credentialDelete(this.descriptor.id, {
            ifMatch: this.descriptor.etag,
            idempotencyKey: generateKey('delete'),
        });
    }

    async toResource(): Promise<ResourceCredential> {
        return this.client.credentials.credentialRevision(this.descriptor.id, this.descriptor.revision);
    }
}
